import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Scale } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = '/api';
const C = { text: '#1A1A18', muted: '#9A9590', accent: '#C67A5C', border: '#E8E3DB' };

export function WeighInModal({ open, onClose, unitPref, onSaved }) {
  const { authHeaders } = useAuth();
  const [weight, setWeight] = useState('');
  const [saving, setSaving] = useState(false);
  const unit = unitPref === 'imperial' ? 'lbs' : 'kg';

  if (!open) return null;

  const handleSave = async () => {
    const val = parseFloat(weight);
    if (!val || val < 30 || val > 999) { toast.error('Enter a valid weight'); return; }
    setSaving(true);
    try {
      // Convert to kg if user enters in lbs
      const weightKg = unit === 'lbs' ? val / 2.205 : val;
      await axios.post(`${API}/weight/log`, { weight_kg: weightKg }, authHeaders());
      toast.success('Weight logged');
      window.dispatchEvent(new Event('weight-logged'));
      onSaved?.();
      onClose();
    } catch (e) { toast.error('Failed to save weight'); }
    setSaving(false);
  };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}
      onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
      <div onClick={e => e.stopPropagation()} style={{
        position: 'relative', width: '100%', maxWidth: 420, background: '#FDFBF7',
        borderRadius: 24, padding: '24px 24px 32px', boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
      }}>
        <button data-testid="weighin-close" onClick={onClose}
          style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer' }}>
          <X style={{ width: 20, height: 20, color: C.muted }} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Scale style={{ width: 20, height: 20, color: '#E5A84B' }} />
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: C.text, margin: 0 }}>
            Quick Weigh-In
          </h2>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: C.muted, fontFamily: "'DM Sans', sans-serif", display: 'block', marginBottom: 6 }}>
            Current weight ({unit})
          </label>
          <input
            data-testid="weighin-input"
            type="number"
            value={weight}
            onChange={e => setWeight(e.target.value)}
            placeholder={unit === 'lbs' ? '165' : '75'}
            autoFocus
            style={{
              width: '100%', padding: '14px 16px', borderRadius: 12, fontSize: 18, fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif", color: C.text, background: '#fff',
              border: `1px solid ${C.border}`, outline: 'none', textAlign: 'center',
            }}
          />
        </div>

        <button
          data-testid="weighin-save"
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%', padding: '14px', borderRadius: 14, border: 'none',
            background: C.accent, color: '#fff', fontSize: 15, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>,
    document.body
  );
}
